import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFetch, useDebounced } from "@/hooks/useFetch";
import { dateTime } from "@/lib/format";
import { DemoBadge, EmptyState, ErrorState, PageHeader, Pager, StatusBadge, TableSkeleton, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const dq = useDebounced(q, 300);
  useEffect(() => { document.title = "Customers · Admin"; }, []);
  const query = useMemo(() => ({ q: dq, page: Number(params.get("page") || 1), page_size: 25 }), [dq, params]);
  const { data, loading, error, reload } = useFetch("/admin/customers", query);
  const setParam = (k, v) => { const n = new URLSearchParams(params); if (!v) n.delete(k); else n.set(k, v); if (k !== "page") n.delete("page"); setParams(n, { replace: true }); };

  return (
    <div data-testid="admin-customers-page">
      <PageHeader eyebrow="Customers" title="Client directory" description="Registered clients with their profile, addresses, consents, wishlist and bag. Viewing a profile is recorded in the audit log. Full CRM and clienteling arrive in Phase 5." />
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => { setQ(e.target.value); setParam("q", e.target.value); }} placeholder="Search by name or email…" className="pl-9 bg-card rounded-lg" data-testid="customers-search-input" />
      </div>
      <div className="bg-card border rounded-xl overflow-hidden">
        {loading && !data ? <TableSkeleton cols={6} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data?.items?.length ? <EmptyState title="No clients yet" description="Customers appear here once they register on the storefront." /> : (
          <Table data-testid="admin-data-table">
            <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>Client</TableHead><TableHead className={thCls}>Country</TableHead><TableHead className={thCls}>Status</TableHead><TableHead className={thCls}>Source</TableHead><TableHead className={thCls}>Last sign in</TableHead><TableHead className={thCls}>Registered</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.map((c) => (
                <TableRow key={c.id} className={`${rowCls} cursor-pointer`} onClick={() => navigate(`/admin/customers/${c.id}`)} data-testid="customer-row">
                  <TableCell className={tdCls}><p className="font-medium inline-flex items-center gap-2">{c.first_name} {c.last_name}{c.is_demo && <DemoBadge />}</p><p className="text-xs text-muted-foreground">{c.email}</p></TableCell>
                  <TableCell className={tdCls}>{c.country_code || "—"}</TableCell>
                  <TableCell className={tdCls}><StatusBadge status={c.status} />{c.email_verified && <StatusBadge status="verified" label="Verified" tone="good" className="ml-1" />}</TableCell>
                  <TableCell className={`${tdCls} text-xs text-muted-foreground`}>{c.source || "storefront"}</TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{c.last_login_at ? dateTime(c.last_login_at) : "Never"}</TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{dateTime(c.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {data && <div className="mt-3"><Pager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={(p) => setParam("page", String(p))} /></div>}
    </div>
  );
}
